/**
 * EntityGraphPanel — V23.14.22
 *
 * Graphe radial des connexions cross-module d'une entité :
 *  - nœud central : l'acteur
 *  - satellites : risks, chains, opps, signals, re_assets, brvm, capital, umaps
 *  - arêtes pondérées par récence + criticité (épaisseur visuelle)
 *
 * Layout : circulaire autour du centre, regroupé par TYPE pour une lecture
 * immédiate (risques en haut, signaux à droite, opportunités en bas, etc.)
 * Cliquer un satellite navigue vers le module concerné.
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { ReactFlow, Background, Controls, MarkerType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { api } from "@/lib/api";
import { Network, Loader2, RefreshCw, ExternalLink } from "lucide-react";

// Palette par type
const TYPE_STYLE = {
  entity:   { color: "#EFD127", bg: "#3a2f00", label: "Entité",   priority: 0 },
  risk:     { color: "#E24B4A", bg: "#391010", label: "Risque",   priority: 1 },
  chain:    { color: "#EF9F27", bg: "#3a2810", label: "Chaîne",   priority: 2 },
  opp:      { color: "#1D9E75", bg: "#0a2d22", label: "Opp.",     priority: 3 },
  signal:   { color: "#7F77DD", bg: "#1d1a3a", label: "Signal",   priority: 4 },
  re_asset: { color: "#4A89DC", bg: "#0d1f3a", label: "Immo",     priority: 5 },
  brvm:     { color: "#378ADD", bg: "#0d1f3a", label: "Marchés fin.", priority: 6 },
  capital:  { color: "#52B0E0", bg: "#0e2d3d", label: "Capital",  priority: 7 },
  umap:     { color: "#A1A1AA", bg: "#1e1e22", label: "Carto.",   priority: 8 },
};

const ROUTE_BY_TYPE = {
  risk: "/app/risques",
  chain: "/app/chainage",
  opp: "/app/origination",
  signal: "/app/veille",
  re_asset: "/app/realestate",
  brvm: "/app/brvm",
  capital: "/app/sources-capital",
  umap: "/app/utility-mapping",
};

function computeRadialLayout(nodes) {
  const central = nodes.find((n) => n.type === "entity");
  const sats = nodes.filter((n) => n.type !== "entity");
  // Group by type then position around a circle
  const groups = {};
  sats.forEach((n) => {
    (groups[n.type] = groups[n.type] || []).push(n);
  });
  const types = Object.keys(groups).sort(
    (a, b) => (TYPE_STYLE[a]?.priority ?? 99) - (TYPE_STYLE[b]?.priority ?? 99)
  );
  const total = Math.max(1, sats.length);
  const radius = Math.max(220, 80 + total * 10);
  // Centre déterministe sur le nœud entité
  const positions = central ? { [central.id]: { x: 0, y: 0 } } : {};

  let cursor = 0;
  types.forEach((t) => {
    const items = groups[t];
    items.forEach((n) => {
      const angle = (cursor / total) * 2 * Math.PI - Math.PI / 2;
      positions[n.id] = {
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
      };
      cursor += 1;
    });
  });
  return positions;
}

function SatelliteNode({ data }) {
  const meta = TYPE_STYLE[data.type] || TYPE_STYLE.signal;
  const isCentral = data.type === "entity";
  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${meta.color}33 0%, ${meta.bg} 100%)`,
        border: `${isCentral ? 3 : 1.5}px solid ${meta.color}`,
        borderRadius: isCentral ? 14 : 8,
        padding: isCentral ? "12px 16px" : "6px 10px",
        minWidth: isCentral ? 200 : 140,
        maxWidth: isCentral ? 240 : 180,
        color: "white",
        fontSize: isCentral ? 13 : 11,
        boxShadow: isCentral
          ? `0 0 0 5px ${meta.color}22, 0 8px 24px rgba(0,0,0,0.5)`
          : `0 2px 8px ${meta.color}44`,
      }}
      data-testid={`graph-node-${data.type}`}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <div
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ background: meta.color }}
        />
        <span
          className="text-[9px] uppercase tracking-wider font-semibold"
          style={{ color: meta.color }}
        >
          {meta.label}
        </span>
        {data.subtype && (
          <span className="text-[9px] text-zinc-400 truncate">· {data.subtype}</span>
        )}
      </div>
      <div className={`font-semibold leading-tight ${isCentral ? "text-base" : "text-[11px]"}`}>
        {data.label}
      </div>
      {!isCentral && data.route && (
        <Link
          to={data.route}
          className="mt-1 text-[9px] text-zinc-400 hover:text-white inline-flex items-center gap-1"
          data-testid={`graph-node-link-${data.id}`}
        >
          Ouvrir <ExternalLink className="w-2.5 h-2.5" />
        </Link>
      )}
    </div>
  );
}

const nodeTypes = { satellite: SatelliteNode };

export default function EntityGraphPanel({ entityId, entityName }) {
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspace/annuaire/entities/${entityId}/graph?limit_per_type=8`);
      setGraph(r.data);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    if (expanded && entityId) load();
  }, [expanded, entityId, load]);

  const { rfNodes, rfEdges } = useMemo(() => {
    if (!graph) return { rfNodes: [], rfEdges: [] };
    const positions = computeRadialLayout(graph.nodes);
    const nodes = graph.nodes.map((n) => ({
      id: n.id,
      type: "satellite",
      position: positions[n.id] || { x: 0, y: 0 },
      data: {
        ...n,
        route: ROUTE_BY_TYPE[n.type] || null,
      },
      draggable: true,
    }));
    const edges = graph.edges.map((e, i) => ({
      id: `e${i}`,
      source: e.source,
      target: e.target,
      animated: e.weight > 0.85,
      style: {
        stroke: TYPE_STYLE[graph.nodes.find((n) => n.id === e.target)?.type]?.color || "#777",
        strokeWidth: 1 + e.weight * 2.5,
        opacity: 0.5 + e.weight * 0.5,
      },
      markerEnd: { type: MarkerType.ArrowClosed },
    }));
    return { rfNodes: nodes, rfEdges: edges };
  }, [graph]);

  return (
    <div className="noam-card p-0 overflow-hidden" data-testid="entity-graph-panel">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-white/[0.03] transition-colors"
        data-testid="entity-graph-toggle"
      >
        <div className="flex items-center gap-2">
          <Network className="w-4 h-4 text-[#EFD127]" />
          <span className="text-[11px] uppercase tracking-wider text-zinc-300 font-semibold">
            Graphe de connaissances
          </span>
          {graph?.stats && (
            <span className="text-[10px] text-zinc-500">
              · {graph.stats.total_satellites} liaisons
            </span>
          )}
        </div>
        <span className="text-[10px] text-zinc-500">{expanded ? "Réduire ▲" : "Déployer ▼"}</span>
      </button>

      {expanded && (
        <div className="border-t border-white/5">
          {loading && (
            <div className="p-6 text-center text-zinc-400 text-xs flex items-center justify-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Construction du graphe…
            </div>
          )}
          {!loading && graph && graph.stats.total_satellites === 0 && (
            <div className="p-6 text-center text-zinc-500 text-xs italic">
              Aucune liaison cross-module pour cette entité. Plus elle est citée dans vos risques, chaînes ou opportunités, plus son graphe s&apos;enrichira.
            </div>
          )}
          {!loading && graph && graph.stats.total_satellites > 0 && (
            <>
              {/* Légende */}
              <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-white/5 bg-black/30 text-[10px] text-zinc-400">
                {Object.entries(graph.stats.by_type).map(([t, n]) => (
                  <span key={t} className="inline-flex items-center gap-1">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: TYPE_STYLE[t]?.color || "#777" }}
                    />
                    <span>{TYPE_STYLE[t]?.label || t}</span>
                    <span className="text-zinc-600">· {n}</span>
                  </span>
                ))}
                <button
                  onClick={load}
                  className="ml-auto text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1"
                  data-testid="entity-graph-refresh"
                >
                  <RefreshCw className="w-3 h-3" /> Rafraîchir
                </button>
              </div>
              <div style={{ height: 520 }}>
                <ReactFlow
                  nodes={rfNodes}
                  edges={rfEdges}
                  nodeTypes={nodeTypes}
                  fitView
                  fitViewOptions={{ padding: 0.2 }}
                  colorMode="dark"
                  proOptions={{ hideAttribution: true }}
                >
                  <Background gap={16} color="#1f2937" />
                  <Controls />
                </ReactFlow>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
